































import { Component, Prop } from 'vue-property-decorator';
import DxDataSource from 'devextreme/data/data_source';

import container from '@/container';
import { Page } from '@/core/components/controls';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { DataSource } from '@/types';
import { StructuredResult, PlannedOperation, Vehicle } from '@/dto';
import {
    CONTRACTS,
    FetchServiceContract,
    WindowsServiceContract,
    EventsServiceContract,
    Events,
    CustomEvents
    } from '@/core/contracts';
import { ContextsState } from '@/store';
import { encodeParams, lookupField } from '@/core/helpers/utils';
import { Table, TableOptions, getTableCaption } from '@/core/components/table';
import PlannedOperationsTable from './PlannedOperationsTable';
import { PlannedOperationsPageArgs, OperationsChartPageArgs } from './types';
import Query from 'devextreme/data/query';
import { duration } from '@/core/filters/duration';

@Component({
    components: {
        TableOptions,
        PlannedOperationsTable,
    },
})
export default class PlannedOperationsPage extends Page {
    @Prop({ default: () => ({}) }) public args!: PlannedOperationsPageArgs;

    public table: Table | null = null;
    public items: PlannedOperation[] = [];
    public vehicles: Vehicle[] = [];
    public tableKey: string = 'planned-operations-control';

    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    public setTable(table: Table): void {
        this.table = table;
    }

    public get tableName(): string {
        return getTableCaption(this.tableKey);
    }

    public get dataSource(): DataSource {
        return new DxDataSource({
            load: this.load,
        });
    }

    public async mounted() {
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public async destroyed() {
        this._eventsService?.raise(CustomEvents.PlannedOperationsPageClosed, null);
    }

    public get vehicleDataSource() {
        if (this.vehicles.length === 0) {
            return null;
        }

        return this.vehicles.map(x => {
            return {
                id: x.id,
                name: x.name
            };
        }).sort((a, b) => {
                return a.name < b.name ? -1 : 1;
        });
    }

    public get filteredData(): PlannedOperation[] {
        const filterExpr = this.table!.getCombinedFilter();
        return filterExpr
            ? Query(this.items).filter(filterExpr).toArray()
            : this.items;
    }

    public showOperationsChart() {
        const args = this.getOperationsChartArgs();
        this._windowsService!.open('operations-chart-page', args);
    }

    public showResourcesUsage() {
        this._windowsService!.open('resources-usage-page', {
            type: ['Loading', 'Unloading', 'Transshipment'],
            rawData: this.filteredData,
            onlyChart: true,
        });
    }

    private getOperationsChartArgs(): OperationsChartPageArgs {
        return {
            operations: this.filteredData,
            parent: this
        };
    }

    private resized() {
        const tableComponent: any = this.$refs['planned-operations-table'];
        const tableControl = tableComponent.$refs['table-control'];

        if (tableComponent && tableComponent.resized) {
            tableComponent.resized();
        }
    }

    private transformData(data: StructuredResult<PlannedOperation>): any {
        if (!(data && data.items && data.items.length > 0)) {
            return [];
        }

        if (this.vehicles.length === 0) {
            this.vehicles = Object.values(data.vehicles);
        }

        return  data.items.map((item) => {
                lookupField(item, 'mainResource', data.vehicles);
                lookupField(item, 'startSite', data.sites);
                lookupField(item, 'finishSite', data.sites);
                lookupField(item, 'type', data.strings);
                lookupField(item, 'linkedResource', data.vehicles);
                lookupField(item, 'resourceNames', data.vehicles);
                item.mainResourceType = data.resourceTypes[item.mainResource.typeId];
                if (item.linkedResource) {
                    item.linkedResourceType = data.resourceTypes[item.linkedResource.typeId];
                }

                const productTypesId = (item.productTypesId !== undefined) ? item.productTypesId : [];
                const productTypes = [];
                for (const i of productTypesId) {
                    productTypes.push(data.resourceTypes[i].name);
                }
                item.productTypes = productTypes.join(', ');

                const areaPlacesId = (item.areaPlacesId !== undefined) ? item.areaPlacesId : [];
                const areaPlaces = [];
                for (const i of areaPlacesId) {
                    areaPlaces.push(data.strings[i]);
                }
                item.areaPlaces = areaPlaces.join(', ');

                // duration
                if (item.startTime && item.finishTime) {
                    const startDate = new Date(item.startTime);
                    const finishDate = new Date(item.finishTime);
                    item.timeDuration = duration(finishDate, startDate);
                }

                return item;
            });
    }

    private async load(): Promise<PlannedOperation[]> {
        const contextId = ContextsState.currentContextId;
        const url = `/contexts/${contextId}/operations?${encodeParams(this.args)}`;

        this.items = await container
            .get<FetchServiceContract>(CONTRACTS.FetchService)
            .get<StructuredResult<PlannedOperation>>(url)
            .then(this.transformData);

        return this.items ? this.items : [];
    }

    private onProcessingFinished({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.table!.refresh();
        }
    }
}
